import React from 'react';
import { useTranslation } from 'react-i18next';

const ACTION_CONFIG = {
  BUY_NOW:     { bg: 'rgba(239,68,68,.15)',  border: 'rgba(239,68,68,.4)',  text: 'var(--red)' },
  BUY_TRANCHE: { bg: 'rgba(245,158,11,.15)', border: 'rgba(245,158,11,.4)', text: 'var(--amb)' },
  HOLD:        { bg: 'rgba(16,185,129,.15)', border: 'rgba(16,185,129,.4)', text: 'var(--grn)' },
  WAIT:        { bg: 'rgba(59,130,246,.15)', border: 'rgba(59,130,246,.4)', text: '#3b82f6'    },
  MONITOR:     { bg: 'rgba(59,130,246,.15)', border: 'rgba(59,130,246,.4)', text: '#3b82f6'    },
};

function getSubtitle(recommendation) {
  if (!recommendation) return '';
  const { action, label } = recommendation;
  if (action === 'BUY_NOW' && label?.toLowerCase().includes('safety')) return 'Safety Critical · 安全紧急';
  if (action === 'BUY_NOW' && label?.toLowerCase().includes('price'))  return 'Price Risk High · 价格风险高';
  return label || '';
}

function fmtValue(v) {
  if (v >= 100_000_000) return `${Math.round(v / 1_000_000)}M`;
  if (v >= 1_000_000)   return `${(v / 1_000_000).toFixed(1)}M`;
  return v.toLocaleString();
}

function getForecastInterp(deltaPct) {
  if (deltaPct >= 10)  return 'forecast_interp_strong_upside';
  if (deltaPct >= 3)   return 'forecast_interp_moderate_upside';
  if (deltaPct > -3)   return 'forecast_interp_near_spot';
  if (deltaPct >= -10) return 'forecast_interp_moderate_downside';
  return 'forecast_interp_strong_downside';
}

function sizingActionKey(sizing) {
  const { variant, coverageDays } = sizing;
  if (variant === 'BUY_NOW_SAFETY') return 'oracle.sizing_action_buy_safety';
  if (variant === 'BUY_NOW_PRICE')  return 'oracle.sizing_action_buy_price';
  if (variant === 'BUY_TRANCHE')    return 'oracle.sizing_action_buy_tranche';
  if (variant === 'HOLD') {
    return coverageDays > 60
      ? 'oracle.sizing_action_hold_baseline'
      : 'oracle.sizing_action_hold_safety';
  }
  return 'oracle.sizing_action_wait';
}

export default function RecBanner({ recommendation, sizing }) {
  const { t } = useTranslation();
  if (!recommendation) return null;

  const { action } = recommendation;
  const cfg      = ACTION_CONFIG[action] || ACTION_CONFIG.MONITOR;
  const subtitle = getSubtitle(recommendation);
  const actionKey = `oracle.action_${action.toLowerCase()}`;
  const isWait   = action === 'WAIT' || action === 'MONITOR';

  return (
    <div style={{
      position: 'relative',
      background: cfg.bg,
      border: `1px solid ${cfg.border}`,
      borderRadius: 10,
      padding: '18px 24px',
      marginBottom: 16,
      overflow: 'hidden',
    }}>
      {/* Watermark */}
      <div style={{
        position: 'absolute',
        right: 20,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 56,
        color: cfg.text,
        opacity: 0.05,
        fontWeight: 900,
        letterSpacing: '.04em',
        pointerEvents: 'none',
        userSelect: 'none',
        lineHeight: 1,
      }}>
        {action}
      </div>

      {/* Header label */}
      <div style={{ fontSize: 12, color: 'var(--t2)', letterSpacing: '.12em', textTransform: 'uppercase', marginBottom: 4 }}>
        Recommendation
      </div>

      {/* Bilingual action label — always both languages */}
      <div className="oracle-rec-action" style={{ color: cfg.text, marginBottom: subtitle ? 4 : 0 }}>
        {t(actionKey, { lng: 'en' })} · {t(actionKey, { lng: 'zh' })}
      </div>

      {subtitle && (
        <div style={{ fontSize: 12, color: 'var(--t2)' }}>{subtitle}</div>
      )}

      {/* Sizing block */}
      {sizing && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${cfg.border}` }}>

          {/* Line 1 — action + volume + purpose (all variants) */}
          <div className="oracle-sizing-volume" style={{ color: cfg.text, fontWeight: 700, marginBottom: 4 }}>
            {t(sizingActionKey(sizing), { volume: sizing.volumeMT.toLocaleString() })}
          </div>

          {/* Lines 2-4 + math — BUY and HOLD only */}
          {!isWait && (
            <>
              {/* Line 2 — today's spot */}
              <div className="oracle-sizing-context">
                {t('oracle.sizing_spot_line', { spot: sizing.ppSpot.toLocaleString() })}
              </div>

              {/* Line 3 — 7-day expected with delta + interpretation */}
              <div className="oracle-sizing-context">
                {t('oracle.sizing_forecast_line', {
                  ev:          sizing.ev.toLocaleString(),
                  deltaSigned: sizing.delta >= 0 ? '+' : '−',
                  delta:       Math.abs(sizing.delta).toLocaleString(),
                  pctSigned:   sizing.deltaPct >= 0 ? '+' : '−',
                  pct:         Math.abs(sizing.deltaPct).toFixed(1),
                  interp:      t(`oracle.${getForecastInterp(sizing.deltaPct)}`),
                })}
              </div>

              {/* Line 4 — cash needed */}
              <div className="oracle-sizing-context" style={{ marginBottom: 8 }}>
                {t('oracle.sizing_cash_line', { value: fmtValue(sizing.valueCNY) })}
              </div>

              {/* Math breakdown */}
              <div className="oracle-sizing-math">
                {t('oracle.sizing_math_breakdown', {
                  basePct:  Math.round(sizing.baseFactor * 100),
                  pctMult:  sizing.percentileMultiplier.toFixed(1),
                  invMult:  sizing.inventoryMultiplier.toFixed(1),
                  finalPct: Math.round(sizing.finalFactor * 100),
                  monthly:  sizing.monthlyDemandMT.toLocaleString(),
                })}
              </div>

              {/* Inventory note */}
              {sizing.inventoryNote && (
                <div style={{ fontSize: 12, color: 'var(--t2)', fontStyle: 'italic', marginTop: 6 }}>
                  {sizing.inventoryNote === 'high'
                    ? `⚠ ${t('oracle.sizing_inventory_high',     { days: sizing.coverageDays })}`
                    : `ℹ ${t('oracle.sizing_inventory_elevated', { days: sizing.coverageDays })}`}
                </div>
              )}
            </>
          )}

          {/* Watch-for triggers — WAIT only */}
          {isWait && (
            <div style={{ fontSize: 12, color: 'var(--t2)', fontStyle: 'italic', marginTop: 4 }}>
              {t('oracle.sizing_wait_watch')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
